import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { usePresupuesto } from "../hooks/usePresupuesto";
import { PresupuestoNode, PRESUPUESTO_COLS } from "../components/PresupuestoNode";
import { Btn } from "../../../settings/components/SettingsUI";
import { useExcelDownload, BotonExcel } from "../../reportes/pages/BalanceComprobacion";

// Banner inline theme-aware — mismo patrón que usa el resto del sistema
// (AdminPage, ListCatalogo) en vez del Toast de SettingsUI, que está pensado
// para el panel oscuro de Ajustes y no se adapta a tema claro/oscuro.
function Toast({ msg, type }) {
  if (!msg) return null;
  const ok = type !== "error";
  const color = ok ? "#4ade80" : "#f87171";
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      background: ok ? "rgba(74,222,128,0.08)" : "rgba(239,68,68,0.08)",
      border: `1px solid ${ok ? "rgba(74,222,128,0.28)" : "rgba(239,68,68,0.28)"}`,
      borderLeft: `3px solid ${color}`,
      borderRadius: 9, padding: "11px 16px", marginBottom: 16,
      color, fontSize: "0.84rem", fontWeight: 500,
    }}>
      <i className={`fa-solid ${ok ? "fa-circle-check" : "fa-circle-exclamation"}`} />
      <span style={{ color: "var(--text-primary)" }}>{msg}</span>
    </div>
  );
}

const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio",
  "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

function walkTree(nodos, fn) {
  for (const n of nodos) { fn(n); walkTree(n.subcuentas || [], fn); }
}

function calcularTotalFactory(edits) {
  return function calcularTotal(node) {
    let total = 0;
    for (let m = 1; m <= 12; m++) total += Number(edits[`${node.id}-${m}`]) || 0;
    for (const h of node.subcuentas || []) total += calcularTotal(h);
    return total;
  };
}

// Suma en vivo de un mes para un nodo: lo capturado directamente + todo su
// subárbol. Las cuentas con hijas muestran este rollup (subtotal automático)
// en vez de un input, para que al escribir en una hija el padre se actualice solo.
function calcularMesFactory(edits) {
  return function calcularMes(node, mes) {
    let total = Number(edits[`${node.id}-${mes}`]) || 0;
    for (const h of node.subcuentas || []) total += calcularMes(h, mes);
    return total;
  };
}

function filtrarArbol(nodos, texto, soloConPresupuesto, calcularTotal) {
  const t = texto.trim().toLowerCase();
  function filtrarNodo(n) {
    const hijosFiltrados = (n.subcuentas || []).map(filtrarNodo).filter(Boolean);
    const nombreMatch = !t || n.nombre.toLowerCase().includes(t) || n.codigo.toLowerCase().includes(t);
    const cumpleMonto = !soloConPresupuesto || calcularTotal(n) !== 0;
    const esteVisible = nombreMatch && cumpleMonto;
    if (esteVisible || hijosFiltrados.length > 0) {
      return { ...n, subcuentas: hijosFiltrados };
    }
    return null;
  }
  return nodos.map(filtrarNodo).filter(Boolean);
}

function fmtMoney(n) {
  return Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function ToggleSwitch({ checked, onChange, label }) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 9, fontSize: "0.8rem", color: "var(--text-primary)", cursor: "pointer", userSelect: "none" }}>
      <span
        onClick={() => onChange(!checked)}
        style={{
          width: 36, height: 20, borderRadius: 20, flexShrink: 0, position: "relative",
          background: checked ? "#f97316" : "var(--hover-bg)",
          border: `1px solid ${checked ? "#f97316" : "var(--border-card)"}`,
          transition: "background 0.15s, border-color 0.15s",
        }}
      >
        <span style={{
          position: "absolute", top: 1, left: checked ? 17 : 1,
          width: 16, height: 16, borderRadius: "50%", background: "#fff",
          transition: "left 0.15s", boxShadow: "0 1px 2px rgba(0,0,0,0.3)",
        }} />
      </span>
      {label}
    </label>
  );
}

function KpiCard({ icon, color, label, sub, value }) {
  return (
    <div style={{
      background: "var(--bg-card)", border: "1px solid var(--border-card)",
      borderRadius: 12, padding: "16px 18px", display: "flex", alignItems: "center", gap: 14,
    }}>
      <div style={{
        width: 42, height: 42, borderRadius: 10, flexShrink: 0,
        background: `${color}18`, border: `1px solid ${color}30`,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <i className={`fa-solid ${icon}`} style={{ color, fontSize: "1.05rem" }} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: "0.75rem", fontWeight: 700, color }}>{label}</div>
        <div style={{ fontSize: "1.3rem", fontWeight: 700, color: "var(--text-primary)", fontFamily: "monospace" }}>
          {value}
        </div>
        <div style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>{sub}</div>
      </div>
    </div>
  );
}

export default function Presupuesto() {
  const { t } = useTranslation();
  const now = new Date();
  const {
    anio, setAnio, mesCorte, setMesCorte, arbol, totales, loading, saving, guardar,
  } = usePresupuesto(now.getFullYear(), now.getMonth() + 1);

  const [edits, setEdits] = useState({});
  const [busqueda, setBusqueda] = useState("");
  const [soloConPresupuesto, setSoloConPresupuesto] = useState(false);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [toast, setToast] = useState({ msg: "", type: "" });

  const excel = useExcelDownload("/api/finance/presupuesto/excel/", `presupuesto_${anio}.xlsx`);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: "", type: "" }), 3500);
  };

  useEffect(() => {
    const next = {};
    walkTree(arbol, n => {
      for (let m = 1; m <= 12; m++) next[`${n.id}-${m}`] = String(n.meses?.[String(m)] ?? "0");
    });
    setEdits(next);
    setExpandedIds(new Set(arbol.map(n => n.id)));
  }, [arbol]);

  const calcularTotal = useMemo(() => calcularTotalFactory(edits), [edits]);
  const calcularMes = useMemo(() => calcularMesFactory(edits), [edits]);

  const arbolFiltrado = useMemo(
    () => filtrarArbol(arbol, busqueda, soloConPresupuesto, calcularTotal),
    [arbol, busqueda, soloConPresupuesto, calcularTotal],
  );

  useEffect(() => {
    if (!busqueda && !soloConPresupuesto) return;
    const ids = [];
    walkTree(arbolFiltrado, n => ids.push(n.id));
    setExpandedIds(prev => new Set([...prev, ...ids]));
  }, [busqueda, soloConPresupuesto, arbolFiltrado]);

  const onToggle = useCallback((id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const onEditMonth = useCallback((cuentaId, mes, valor) => {
    setEdits(prev => ({ ...prev, [`${cuentaId}-${mes}`]: valor }));
  }, []);

  async function handleGuardar() {
    const lineas = [];
    walkTree(arbol, n => {
      for (let m = 1; m <= 12; m++) {
        const num = Number(edits[`${n.id}-${m}`]);
        const monto = Number.isFinite(num) ? String(Math.round(num * 100) / 100) : "0";
        lineas.push({ cuenta: n.id, mes: m, monto });
      }
    });
    try {
      await guardar(lineas);
      showToast("Presupuesto guardado");
    } catch (e) {
      showToast(e.message, "error");
    }
  }

  const ingresos = totales?.ingresos;
  const gastos = totales?.gastos;
  const resultado = totales?.resultado_presupuestado ?? 0;
  const pctAlcance = totales?.pct_alcance_promedio;

  const ingresosPresup = ingresos?.presupuesto_acumulado ?? ingresos?.presupuesto ?? 0;
  const gastosPresup = gastos?.presupuesto_acumulado ?? gastos?.presupuesto ?? 0;

  return (
    <div>
      <span style={{ color: "var(--text-muted)" }}>
        <Link to="/"><span style={{ color: "var(--text-muted)" }}>Inicio </span></Link>/ <span>Presupuesto</span>
      </span>
      <h2 className="mt-2 me-2 p-0 title">{t("presupuesto.titulo")}</h2>
      <p className="mb-3" style={{ color: "var(--text-muted)" }}>
        {t("presupuesto.administra")}
      </p>

      <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-card)", borderRadius: 14, overflow: "hidden" }}>
        <div style={{ padding: "22px 24px" }}>
          <Toast msg={toast.msg} type={toast.type} />

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
              <div>
                <label style={{ fontSize: "0.72rem", color: "var(--text-muted)", display: "block", marginBottom: 3 }}>Año</label>
                <input type="number" value={anio} onChange={e => setAnio(Number(e.target.value))}
                  style={{ width: 90, background: "var(--bg-input)", border: "1px solid var(--border-input)",
                    borderRadius: 8, padding: "7px 10px", color: "var(--text-primary)", fontSize: "0.85rem" }} />
              </div>
              <div>
                <label style={{ fontSize: "0.72rem", color: "var(--text-muted)", display: "block", marginBottom: 3 }}>Mes de corte (vs. Real)</label>
                <select value={mesCorte} onChange={e => setMesCorte(Number(e.target.value))}
                  style={{ background: "var(--bg-input)", border: "1px solid var(--border-input)",
                    borderRadius: 8, padding: "7px 10px", color: "var(--text-primary)", fontSize: "0.85rem" }}>
                  {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <BotonExcel
                onClick={() => excel.descargar(`anio=${anio}&mes_corte=${mesCorte}`)}
                loading={excel.loading}
                disabled={excel.loading || loading}
              />
              <Btn disabled={saving || loading} onClick={handleGuardar}>
                <i className="fa-solid fa-floppy-disk" />{saving ? "Guardando…" : "Guardar presupuesto"}
              </Btn>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginBottom: 20 }}>
            <KpiCard icon="fa-arrow-trend-up" color="#4ade80" label="Total Ingresos"
              value={`₡${fmtMoney(ingresosPresup)}`} sub={`Presupuesto acumulado a ${MESES[mesCorte - 1]}`} />
            <KpiCard icon="fa-arrow-trend-down" color="#fbbf24" label="Total Gastos"
              value={`₡${fmtMoney(gastosPresup)}`} sub={`Presupuesto acumulado a ${MESES[mesCorte - 1]}`} />
            <KpiCard icon="fa-chart-pie" color="#a78bfa" label="Resultado Presupuestado"
              value={`₡${fmtMoney(resultado)}`} sub="Ingresos − Gastos" />
            <KpiCard icon="fa-bullseye" color="#60a5fa" label="% de Alcance Promedio"
              value={pctAlcance != null ? `${(pctAlcance * 100).toFixed(0)}%` : "—"} sub="Real / Presupuesto" />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
            <div style={{ position: "relative", flex: "1 1 240px", minWidth: 220 }}>
              <i className="fa-solid fa-magnifying-glass" style={{
                position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
                color: "var(--text-muted)", fontSize: "0.8rem",
              }} />
              <input
                placeholder="Buscar categoría o código…"
                value={busqueda}
                onChange={e => setBusqueda(e.target.value)}
                style={{
                  width: "100%", boxSizing: "border-box", background: "var(--bg-input)",
                  border: "1px solid var(--border-input)", borderRadius: 8, padding: "9px 12px 9px 34px",
                  color: "var(--text-primary)", fontSize: "0.84rem",
                }}
              />
            </div>
            <ToggleSwitch checked={soloConPresupuesto} onChange={setSoloConPresupuesto} label="Solo con presupuesto" />
          </div>

          <div style={{
            display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 12,
            fontSize: "0.74rem", color: "var(--text-muted)", background: "var(--hover-bg)",
            border: "1px solid var(--border-card)", borderRadius: 8, padding: "8px 12px",
          }}>
            <i className="fa-solid fa-circle-info" style={{ marginTop: 2 }} />
            <span>
              Podés capturar el monto en cualquier nivel (cuenta, subcuenta o detalle). El
              "Total" de una cuenta suma lo que le pusiste directamente más todo lo de sus
              subcuentas — evitá presupuestar el mismo monto en una cuenta padre y en sus
              hijas, porque se sumaría doble.
            </span>
          </div>

          {loading ? (
            <div style={{ textAlign: "center", padding: 32, color: "var(--text-muted)" }}>
              <i className="fa-solid fa-circle-notch fa-spin" style={{ color: "#f97316", marginRight: 8 }} />Cargando…
            </div>
          ) : arbolFiltrado.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>
              <i className="fa-solid fa-magnifying-glass" style={{ fontSize: "2rem", display: "block", marginBottom: 10, opacity: 0.2 }} />
              <p style={{ margin: 0, fontSize: "0.85rem" }}>Sin resultados para este filtro.</p>
            </div>
          ) : (
            <div style={{ overflowX: "auto", border: "1px solid var(--border-card)", borderRadius: 10 }}>
              <div style={{ minWidth: "max-content" }}>
                <div style={{
                  display: "grid", gridTemplateColumns: PRESUPUESTO_COLS, columnGap: 10,
                  alignItems: "center", padding: "8px 10px",
                  background: "var(--hover-bg)", borderBottom: "1px solid var(--border-card)",
                  fontSize: "0.66rem", fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase",
                }}>
                  <span>Categoría</span>
                  {MESES.map(m => <span key={m} style={{ textAlign: "right" }}>{m.slice(0, 3)}</span>)}
                  <span style={{ textAlign: "right" }}>Total</span>
                  <span style={{ textAlign: "right" }}>Estado</span>
                </div>
                {arbolFiltrado.map(n => (
                  <PresupuestoNode
                    key={n.id} node={n} depth={0}
                    expandedIds={expandedIds} onToggle={onToggle}
                    edits={edits} onEditMonth={onEditMonth}
                    calcularTotal={calcularTotal} calcularMes={calcularMes} conReal
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
